import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { LuCopy, LuRefreshCcw } from 'react-icons/lu';
import { Col, Form, Row } from 'react-bootstrap';
import { DateTime } from 'luxon';

import FORMATTERS from 'helpers/formatters';
import { Button } from '_components/_core';

import { ModalFooter, StyledModal } from './styles';
import { has } from 'lodash';

function PixQrCodeModal({
  invoice: oldInvoice,
  isVisible,
  onModalToggle,
  onRefreshPaymentData,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [showCopiedAlert, setShowCopiedAlert] = useState(false);
  const [invoice, setInvoice] = useState(oldInvoice);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const isCodeExpired = useMemo(() => {
    if (!has(invoice, 'metadata.pix_expire_date')) {
      return false;
    }

    const expireDate = invoice.metadata.pix_expire_date;

    if (!expireDate) {
      return false;
    }

    return DateTime.fromFormat(expireDate, 'yyyy-MM-dd HH:mm:ss') < DateTime.now();
  }, [invoice]);

  const formatDate = useCallback(date => {
    if (!date) {
      return '';
    }

    const datePart = date.split('T')[0];
    const [year, month, day] = datePart.split('-');

    return `${day}/${month}/${year}`;
  }, []);

  useEffect(() => {
    if (!isVisible) {
      setIsLoading(false);
    }
  }, [isVisible]);

  const handleRefreshPaymentData = useCallback(() => {
    setIsRefreshing(true);

    const params = {
      invoice_id: invoice.id,
    };

    const successCallback = data => {
      const { invoice: updatedInvoice } = data;

      setIsRefreshing(false);
      setInvoice(updatedInvoice);
    };

    const errorCallback = () => {
      setIsRefreshing(false);
    };

    onRefreshPaymentData(params, successCallback, errorCallback);
  }, [invoice, onRefreshPaymentData]);

  const renderModalFooter = useCallback(
    () => (
      <ModalFooter>
        <Button variant="secondary" onClick={() => onModalToggle()}>
          Cancelar
        </Button>
        <Button
          variant="secondary"
          onClick={onModalToggle}
          isLoading={isLoading}
          disabled={isLoading}
        >
          Fechar
        </Button>
      </ModalFooter>
    ),
    [onModalToggle, isLoading],
  );

  const handleCopy = async e => {
    e.preventDefault();
    e.stopPropagation();

    await navigator.clipboard.writeText(invoice.metadata.pix_payload);

    setShowCopiedAlert(true);

    setTimeout(() => {
      setShowCopiedAlert(false);
    }, 3000);
  };

  return (
    <StyledModal
      size="sm"
      title="Pagar com Pix"
      isVisible={isVisible}
      toggleModal={onModalToggle}
      footer={renderModalFooter()}
    >
      <Form>
        <Form.Row>
          <Form.Group as={Col} md="6">
            <Form.Label>Número e Situação</Form.Label>
            <p>
              {invoice.provider_invoice_number}
              <span className="ml-2">•</span>
              {FORMATTERS.SUBSCRIPTION_INVOICE_STATUS(invoice.status)}
            </p>
          </Form.Group>
          <Form.Group as={Col} md="6">
            <Form.Label>Total e Vencimento</Form.Label>
            <p>
              <span
                className="text-warning"
                style={{
                  fontWeight: '500',
                }}
              >
                {FORMATTERS.NUMBER(invoice.total_amount)}
              </span>
              <span className="ml-2 mr-2">•</span>
              {formatDate(invoice.due_date)}
            </p>
          </Form.Group>
        </Form.Row>
        {isCodeExpired && (
          <Row>
            <Col>
              <div>
                <small>
                  O código Pix expirou, gere um novo código para efetuar o pagamento.
                </small>
              </div>
              <Button
                variant="default"
                className="d-flex justify-content-center align-items-center mt-3"
                onClick={handleRefreshPaymentData}
                isLoading={isRefreshing}
                disabled={isRefreshing}
              >
                <LuRefreshCcw className="mr-2" />
                Gerar novo QR Code
              </Button>
            </Col>
          </Row>
        )}
        {!isCodeExpired && (
          <Form.Row>
            <Form.Group as={Col} md="12">
              <Form.Label>QR Code Pix</Form.Label>
              <div className="d-flex align-items-center">
                <img
                  width={200}
                  height={200}
                  src={`data:image/jpeg;base64, ${invoice.metadata?.pix_base64}`}
                  alt="QR Code pix"
                />
                <div className="d-flex flex-column justify-content-center align-items-start">
                  <p className="w-50">
                    Acesse seu APP de pagamentos e faça a leitura do QR Code ao lado para
                    efetuar o pagamento de forma rápida e segura.
                  </p>
                  <Button
                    onClick={handleCopy}
                    className="d-flex justify-content-start align-items-center"
                  >
                    <LuCopy className="mr-2" />
                    Pix copia e cola
                  </Button>
                  {showCopiedAlert && (
                    <small className="mt-2 text-success">
                      Código copiado para a área de transferência!
                    </small>
                  )}
                </div>
              </div>
            </Form.Group>
          </Form.Row>
        )}
      </Form>
    </StyledModal>
  );
}

export default PixQrCodeModal;
